import { noteEvent } from "./analytics.js";

// The machine runs in short slices of real time on a timer, whatever the
// display is doing (issue #885). Audio gains most from the fine grain: its
// queue need only cover a slice.
const TickMs = 10;

export const RewindCaptureInterval = 50; // emulated frames, ~1 second

// Under ?audioDebug, one console line per second in which the emulator sat
// idle between ticks or a tick ran long, or the audio queue underran or
// dropped, so a click can be matched to a cause. The sound chip posts samples
// throughout execute(), so only the idle time starves the audio queue.
const AudioDebugLogIntervalMs = 1000;
const AudioDebugSlowTickMs = 30;
const AudioDebugSlowPresentMs = 30;

const VirtualMhzUpdateMs = 3333;

class VirtualSpeedUpdater {
    constructor(cpuSpeed) {
        this.cpuSpeed = cpuSpeed;
        this.cycles = 0;
        this.time = 0;
        this.v = document.querySelector(".virtualMHz");
        this.header = document.getElementById("virtual-mhz-header");
        this.speedy = false;
        this.display();
    }

    update(cycles, time, speedy) {
        this.cycles += cycles;
        this.time += time;
        this.speedy = speedy;
    }

    display() {
        // MRG would be nice to graph instantaneous speed to get some idea where the time goes.
        if (this.cycles) {
            const thisMHz = this.cycles / this.time / 1000;
            this.v.textContent = thisMHz.toFixed(1);
            if (this.cycles >= 10 * this.cpuSpeed) {
                this.cycles = this.time = 0;
            }
            this.header.style.color = this.speedy ? "red" : "white";
        }
        setTimeout(() => this.display(), VirtualMhzUpdateMs);
    }
}

/**
 * Runs the machine in real time: the tick that turns wall-clock time into
 * cycles, starting and stopping, the audio lead, fast-forward and the speed
 * readout. Owns `running`, and dispatches a "running" event whenever it
 * changes hands, "tick" on every timer tick and on every vsync that runs the
 * machine (the first tick only takes the time), and "rewind-capture" every
 * RewindCaptureInterval frames for whoever keeps the rewind history. Anything
 * that needs the machine held still while it works (a dialog, a snapshot, the
 * rewind panel, a hidden tab) takes a `pause()`; the loop runs again once every
 * hold has let go, provided the user still wants it running.
 */
export class EmulationLoop extends EventTarget {
    constructor({
        processor,
        display,
        audioHandler,
        dbgr,
        gamepad,
        keyboard,
        clocksPerSecond,
        cpuSpeed,
        fastTape,
        audioStatsNode,
    }) {
        super();
        this.processor = processor;
        this.display = display;
        this.audioHandler = audioHandler;
        this.dbgr = dbgr;
        this.gamepad = gamepad;
        this.keyboard = keyboard;
        this.clocksPerSecond = clocksPerSecond;
        this.maxCyclesPerTick = clocksPerSecond / 10;
        this.rewindCaptureCycles = (RewindCaptureInterval * clocksPerSecond) / 50;
        this.fastTape = fastTape;
        this.audioStatsNode = audioStatsNode;

        this.running = false;
        this.fastAsPossible = false;
        this.emulatedTo = 0;
        this.lastEnd = 0;
        this.nextTickDue = 0;
        this.tickToken = null;
        this.vsyncToken = null;
        this.emulationLeadMs = 0;
        this.rewindCycleCounter = 0;
        this.wanted = false;
        this.holds = new Set();
        this.resumeOnVisible = null;
        this.lockstep = null;

        this.virtualSpeedUpdater = new VirtualSpeedUpdater(cpuSpeed);
        this.audioDebugLog = { start: 0, ticks: 0, cycles: 0, maxIdle: 0, maxExecute: 0, maxPaint: 0, maxSnapshot: 0 };

        document.addEventListener("visibilitychange", () => this.handleVisibilityChange(), false);
    }

    isRunning() {
        return this.running;
    }

    go() {
        this.wanted = true;
        if (this.holds.size === 0) this.start();
        else
            console.warn(
                `Emulator held by ${[...this.holds].map((hold) => hold.reason).join(", ")}; it runs when they let go`,
            );
    }

    stop(debug) {
        this.wanted = false;
        this.halt();
        if (debug) this.dbgr.debug(this.processor.pc);
    }

    /**
     * Holds the machine stopped until the returned function is called. Holds
     * nest, and letting go twice counts once. `reason` names the holder when a
     * go() has to wait, so a hold that is never released can be found.
     */
    pause(reason) {
        if (this.holds.size === 0 && this.running) this.halt();
        const hold = { reason };
        this.holds.add(hold);
        return () => {
            if (!this.holds.delete(hold)) return;
            if (this.holds.size === 0 && this.wanted) this.start();
        };
    }

    start() {
        if (this.running) return;
        this.audioHandler.unmute();
        this.running = true;
        this.dispatchEvent(new Event("running"));
        this.run();
    }

    halt() {
        this.running = false;
        this.dispatchEvent(new Event("running"));
        this.processor.stop();
        this.audioHandler.mute();
    }

    run() {
        this.nextTickDue = 0;
        this.scheduleTick(0);
        this.scheduleVsyncTick();
    }

    toggleFastAsPossible() {
        this.fastAsPossible = !this.fastAsPossible;
    }

    /**
     * Hands the running of the machine to a shared session's lockstep
     * (src/lockstep.js), whose `execute(cycles)` stands in for the processor's, or
     * back with null. While it lasts the loop runs nothing only this machine would:
     * no speedy frame skip, no gamepad keys, and no pause in a hidden tab.
     */
    setLockstep(lockstep) {
        this.lockstep = lockstep;
        if (lockstep && this.resumeOnVisible) {
            this.resumeOnVisible();
            this.resumeOnVisible = null;
        }
    }

    execute(cycles) {
        return this.lockstep ? this.lockstep.execute(cycles) : this.processor.execute(cycles);
    }

    // A user-blocking task runs ahead of rendering and ordinary timers, so a stuck
    // compositor does not hold the tick off too.
    scheduleTick(delayMs) {
        const token = (this.tickToken = {});
        const fire = () => {
            if (this.tickToken === token) this.tick();
        };
        if (window.scheduler?.postTask) window.scheduler.postTask(fire, { delay: delayMs, priority: "user-blocking" });
        else window.setTimeout(fire, delayMs);
    }

    // Booked from the previous due time, not from now, so the period averages
    // TickMs and a flyback lands at the same point of a tick every frame. A tick
    // a whole period late starts afresh rather than a burst of catch-up ticks.
    nextTickDelay(now) {
        if (now - this.nextTickDue > TickMs) this.nextTickDue = now;
        this.nextTickDue += TickMs;
        return this.nextTickDue - now;
    }

    scheduleVsyncTick() {
        const token = (this.vsyncToken = {});
        window.requestAnimationFrame((vsyncTime) => {
            if (this.vsyncToken !== token || !this.running) return;
            this.scheduleVsyncTick();
            this.vsyncTick(vsyncTime);
        });
    }

    // Emulates up to the vsync and presents at once, so a flyback is shown at the
    // first vsync after it; two share a refresh only if a timer tick reached the
    // next flyback before this callback ran.
    vsyncTick(vsyncTime) {
        // While speedy the timer ticks run flat out.
        if (this.emulatedTo !== 0 && !this.isSpeedy() && vsyncTime > this.emulatedTo) this.advance(vsyncTime, false);
        this.display.present(vsyncTime);
    }

    isSpeedy() {
        if (this.lockstep) return false;
        return this.fastAsPossible || (this.fastTape && this.processor.tapeInterface.motorOn);
    }

    tick() {
        if (!this.running) {
            this.emulatedTo = 0;
            return;
        }
        const now = performance.now();
        const speedy = this.isSpeedy();
        this.display.setSpeedy(speedy);
        this.scheduleTick(speedy ? 0 : this.nextTickDelay(now));
        this.advance(now, speedy);
    }

    advance(now, speedy) {
        // now can be a vsync timestamp, before this ran; the timings use start.
        const start = performance.now();
        const { processor, display, audioHandler } = this;
        if (!this.lockstep) this.gamepad.update(processor.sysvia);
        this.dispatchEvent(new Event("tick"));
        if (this.emulatedTo !== 0) {
            let cycles;
            if (!speedy) {
                const sinceLast = Math.max(0, now - this.emulatedTo);
                cycles = (sinceLast * this.clocksPerSecond) / 1000;
                cycles = Math.min(cycles, this.maxCyclesPerTick);
            } else {
                cycles = this.clocksPerSecond / 50;
            }
            cycles |= 0;
            try {
                if (!this.execute(cycles)) {
                    this.stop(true);
                }
                audioHandler.flushChipEvents();
                const end = performance.now();
                this.virtualSpeedUpdater.update(cycles, end - start, speedy);
                const paintMs = display.takePaintMs();
                let snapshotMs = 0;
                this.rewindCycleCounter += cycles;
                if (this.rewindCycleCounter >= this.rewindCaptureCycles) {
                    this.rewindCycleCounter -= this.rewindCaptureCycles;
                    this.dispatchEvent(new Event("rewind-capture"));
                    snapshotMs = performance.now() - end;
                }
                if (this.audioStatsNode)
                    this.logAudioDebugTick(
                        start,
                        cycles,
                        speedy ? 0 : start - this.lastEnd,
                        end - start,
                        paintMs,
                        snapshotMs,
                    );
            } catch (e) {
                this.running = false;
                noteEvent("exception", "thrown", e.stack);
                this.dbgr.debug(processor.pc);
                throw e;
            }
            if (this.keyboard.postFrameShouldPause()) {
                this.stop(false);
            }
        }
        this.emulatedTo = Math.max(this.emulatedTo, now);
        this.lastEnd = performance.now();
    }

    // A change of audio buffer depth is taken by the picture, not the sound:
    // gaining lead emulates ahead at once; losing it moves `emulatedTo` forward
    // so the ticks emulate nothing until the queue has drained by that much.
    setEmulationLead(leadMs) {
        if (!this.running) return;
        const aheadMs = leadMs - this.emulationLeadMs;
        this.emulationLeadMs = leadMs;
        if (aheadMs > 0) {
            if (!this.execute((aheadMs * this.clocksPerSecond) / 1000)) this.stop(true);
            this.audioHandler.flushChipEvents();
        } else {
            this.emulatedTo -= aheadMs;
        }
    }

    handleVisibilityChange() {
        const { processor } = this;
        if (document.visibilityState === "hidden") {
            if (this.lockstep) return;
            const keepRunningWhenHidden =
                processor.tapeInterface.motorOn || processor.fdc.motorOn[0] || processor.fdc.motorOn[1];
            if (!keepRunningWhenHidden && !this.resumeOnVisible) this.resumeOnVisible = this.pause("the hidden tab");
        } else if (this.resumeOnVisible) {
            this.resumeOnVisible();
            this.resumeOnVisible = null;
        }
    }

    logAudioDebugTick(now, cycles, idleMs, executeMs, paintMs, snapshotMs) {
        const log = this.audioDebugLog;
        if (log.start === 0) log.start = now;
        log.ticks++;
        log.cycles += cycles;
        log.maxIdle = Math.max(log.maxIdle, idleMs);
        log.maxExecute = Math.max(log.maxExecute, executeMs);
        log.maxPaint = Math.max(log.maxPaint, paintMs);
        log.maxSnapshot = Math.max(log.maxSnapshot, snapshotMs);
        if (now - log.start < AudioDebugLogIntervalMs) return;
        const audio = this.audioHandler.takeEventCounts();
        const present = this.display.takePresentMs();
        const leadMin = Number.isFinite(audio.leadMinMs) ? `${audio.leadMinMs.toFixed(1)}ms` : "(no stats)";
        if (
            log.maxIdle > AudioDebugSlowTickMs ||
            log.maxExecute > AudioDebugSlowTickMs ||
            present > AudioDebugSlowPresentMs ||
            audio.stall ||
            audio.skip
        ) {
            console.log(
                `${(now / 1000).toFixed(0)}s: ${log.ticks} ticks emulating ${((1000 * log.cycles) / this.clocksPerSecond).toFixed(0)}ms, ` +
                    `idle max ${log.maxIdle.toFixed(0)}ms, ` +
                    `execute max ${log.maxExecute.toFixed(0)}ms (paint ${log.maxPaint.toFixed(1)}ms), ` +
                    `present max ${present.toFixed(0)}ms, snapshot ${log.maxSnapshot.toFixed(1)}ms; ` +
                    `audio lead min ${leadMin}, stalls ${audio.stall}, skipped ${audio.skip.toFixed(0)}ms`,
            );
        }
        log.start = now;
        log.ticks = log.cycles = log.maxIdle = log.maxExecute = log.maxPaint = log.maxSnapshot = 0;
    }

    benchmarkCpu(numCycles) {
        numCycles = numCycles || 10 * 1000 * 1000;
        const oldFS = this.display.frameSkip;
        this.display.frameSkip = 1000000;
        const startTime = performance.now();
        this.processor.execute(numCycles);
        const endTime = performance.now();
        this.display.frameSkip = oldFS;
        const msTaken = endTime - startTime;
        const virtualMhz = numCycles / msTaken / 1000;
        console.log("Took " + msTaken + "ms to execute " + numCycles + " cycles");
        console.log("Virtual " + virtualMhz.toFixed(2) + "MHz");
    }

    benchmarkVideo(numCycles) {
        numCycles = numCycles || 10 * 1000 * 1000;
        const oldFS = this.display.frameSkip;
        this.display.frameSkip = 1000000;
        const startTime = performance.now();
        this.display.video.polltime(numCycles);
        const endTime = performance.now();
        this.display.frameSkip = oldFS;
        const msTaken = endTime - startTime;
        const virtualMhz = numCycles / msTaken / 1000;
        console.log("Took " + msTaken + "ms to execute " + numCycles + " video cycles");
        console.log("Virtual " + virtualMhz.toFixed(2) + "MHz");
    }

    profileCpu(arg) {
        console.profile("CPU");
        this.benchmarkCpu(arg);
        console.profileEnd();
    }

    profileVideo(arg) {
        console.profile("Video");
        this.benchmarkVideo(arg);
        console.profileEnd();
    }
}
